import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  ChevronRight, ArrowLeft, CheckCircle2,
  Star, Trophy, BookOpen, Play, Sparkles
} from 'lucide-react';
import axios from 'axios';
import { interviewModules } from '../data/interviewModules';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CabinInterviewPrep() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [progress, setProgress] = useState({ completed_lessons: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API}/cabin-training/progress`, { withCredentials: true });
      setProgress(response.data || { completed_lessons: [] });
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const getModuleProgress = (module) => {
    const completedLessons = progress?.completed_lessons || [];
    let completed = 0;
    let total = 0;
    
    module.units?.forEach(unit => {
      unit.lessons?.forEach(lesson => {
        total++;
        if (completedLessons.includes(lesson.lesson_id)) {
          completed++;
        }
      });
    });
    
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const getTotalCompleted = () => {
    const completedLessons = progress?.completed_lessons || [];
    let count = 0;
    interviewModules.forEach(module => {
      module.units?.forEach(unit => {
        unit.lessons?.forEach(lesson => {
          if (completedLessons.includes(lesson.lesson_id)) {
            count++;
          }
        });
      });
    });
    return count;
  };

  const getTotalLessons = () => {
    let count = 0;
    interviewModules.forEach(module => {
      module.units?.forEach(unit => {
        count += unit.lessons?.length || 0;
      });
    });
    return count;
  };

  const isModuleCompleted = (moduleId) => {
    const module = interviewModules.find(m => m.id === moduleId);
    if (!module) return false;
    return getModuleProgress(module) === 100;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-600 to-pink-600 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-bounce text-6xl mb-4">🎯</div>
          <p className="text-white font-medium">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const totalCompleted = getTotalCompleted();
  const totalLessons = getTotalLessons();
  const overallProgress = totalLessons > 0 ? Math.round((totalCompleted / totalLessons) * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-600 via-pink-500 to-rose-500 px-4 pt-6 pb-8 rounded-b-[2rem] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24" />
        
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="flex items-center justify-between mb-6">
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Ana Menü</span>
            </button>
            
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur px-3 py-1.5 rounded-full">
                <Trophy className="w-4 h-4 text-yellow-300" />
                <span className="text-white font-bold text-sm">{totalCompleted}</span>
              </div>
              <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur px-3 py-1.5 rounded-full">
                <Star className="w-4 h-4 text-yellow-300" />
                <span className="text-white font-bold text-sm">{totalLessons} Ders</span>
              </div>
            </div>
          </div>

          <div className="text-center text-white mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="w-6 h-6 text-yellow-300" />
              <h1 className="text-2xl font-bold">Mülakat Hazırlık Merkezi</h1>
              <Sparkles className="w-6 h-6 text-yellow-300" />
            </div>
            <p className="text-purple-100 text-sm">Havayolu mülakatlarına profesyonel hazırlık</p>
          </div>

          <div className="bg-white rounded-2xl p-4 shadow-xl">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-gray-500 text-sm">Genel İlerleme</p>
                <p className="text-xl font-bold text-gray-800">
                  {totalCompleted} / {totalLessons} Ders
                </p>
              </div>
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <span className="text-xl font-bold text-white">
                  {overallProgress}%
                </span>
              </div>
            </div>
            <Progress value={overallProgress} className="h-2" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 mt-6">
        
        {/* Modules Header */}
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-purple-600" />
          Eğitim Modülleri
        </h2>
        
        <div className="space-y-4">
          {interviewModules.map((module, index) => {
            const moduleProgress = getModuleProgress(module);
            const completed = isModuleCompleted(module.id);
            
            return (
              <Card 
                key={module.id}
                className="overflow-hidden transition-all duration-300 border-0 shadow-md cursor-pointer hover:shadow-xl hover:scale-[1.01]"
                onClick={() => navigate(`/interview-prep/module/${module.module_id}`)}
              >
                <CardContent className="p-0">
                  <div className="flex items-center">
                    <div className={`w-20 h-28 bg-gradient-to-br ${module.color} flex items-center justify-center relative`}>
                      <span className="text-4xl">{module.icon}</span>
                      {completed && (
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                          <CheckCircle2 className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Modül {index + 1}</span>
                            {completed && (
                              <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                                ✓ Tamamlandı
                              </span>
                            )}
                          </div>
                          <h3 className="font-bold text-gray-900 text-lg">{module.title}</h3>
                          <p className="text-sm text-gray-500 mt-0.5">{module.subtitle}</p>
                          
                          {moduleProgress > 0 && !completed && (
                            <div className="mt-3">
                              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                                <span>{moduleProgress}% tamamlandı</span>
                              </div>
                              <Progress value={moduleProgress} className="h-1.5" />
                            </div>
                          )}
                          
                          <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                            <span className="flex items-center gap-1">
                              <BookOpen className="w-3.5 h-3.5" />
                              {module.totalUnits} ünite
                            </span>
                            <span className="flex items-center gap-1">
                              <Play className="w-3.5 h-3.5" />
                              ~{module.estimatedTime} dk
                            </span>
                          </div>
                        </div>
                        
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card className="mt-6 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <span className="text-lg">💡</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Nasıl Çalışmalısınız?</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Önce <strong>İnteraktif Pratik Merkezi</strong>'nden başlayın. 
                  Sonra modülleri sırayla tamamlayın: <strong>Mülakat Temelleri</strong>, 
                  <strong>Grup ve Birebir Mülakat</strong>, ardından başvuracağınız <strong>havayolunun rehberini</strong> çalışın.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
